/**
 * trialService.ts — Sistema de trial Pro 60 días para nuevos usuarios.
 *
 * Los trials se almacenan en Firestore:  proTrials/{uid}
 *
 * Campos del documento:
 *   active:          boolean    — true mientras el trial esté vigente
 *   createdAt:       Timestamp  — fecha de creación
 *   expiresAt:       Timestamp  — fecha de expiración (createdAt + 60 días)
 *   usedFirstTrial:  boolean    — true si este uid ya activó un trial alguna vez
 *   notes:           string     — nota informativa
 *
 * Prioridad de planes:
 *   internalEntitlement activo > Google Play Billing activo > Trial activo > Free
 */

import { db } from '../firebase';
import {
  doc, collection, getDoc, setDoc, updateDoc,
  serverTimestamp, Timestamp
} from 'firebase/firestore';

// ── Constantes ──
export const TRIAL_DAYS = 60;

// ── Tipos ──
export interface TrialData {
  active: boolean;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  usedFirstTrial: boolean;
  notes?: string;
  ownerEmail?: string | null;
}

export interface TrialStatus {
  hasActiveTrial: boolean;      // trial existe y no ha expirado
  trialExpiresAt: Date | null;  // fecha de expiración
  daysRemaining: number;        // días restantes (0 si expirado)
  hasUsedTrial: boolean;        // si este uid ya tuvo trial alguna vez
}

// ── Crear trial Pro para usuario nuevo ──
// NON-BLOCKING: si falla, loguea warning y permite continuar el registro.
// Se verifica si ya existe un trial para evitar duplicados.
export async function createProTrial(uid: string, email?: string | null): Promise<boolean> {
  try {
    const trialRef = doc(collection(db, 'proTrials'), uid);

    // Verificar si ya tiene trial — no crear duplicado
    const existing = await getDoc(trialRef);
    if (existing.exists()) {
      console.log('[TRIAL] Trial already exists for uid:', uid, '— skipping creation');
      return false;
    }

    // Calcular expiresAt en cliente:
    // ⚠️ TODO: En una versión más robusta, calcular en Cloud Function para evitar
    // manipulación de reloj en el dispositivo. Para este MVP se calcula en cliente.
    const expirationDate = new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
    const expiresAt = Timestamp.fromDate(expirationDate);

    await setDoc(trialRef, {
      active: true,
      createdAt: serverTimestamp(),
      expiresAt,
      usedFirstTrial: true, // Este uid ya ha usado su primer trial automático
      notes: 'Auto trial on first signup',
      ownerEmail: email ?? null
    });

    console.log('[TRIAL] Trial Pro created for:', uid, '— expires:', expirationDate.toLocaleDateString('es-ES'));
    return true;
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      // Parar y reportar — requiere actualizar Firestore rules
      console.error('[TRIAL] ⛔ PERMISSION DENIED al crear proTrials. Revisar Firestore rules antes de continuar:', err.message);
    } else {
      console.warn('[TRIAL] Failed to create trial (non-blocking):', err);
    }
    return false;
  }
}

// ── Leer el estado del trial para un usuario ──
// Si el trial ha caducado, lo marca como inactive en Firestore.
// Si hay permission-denied, loguea error y devuelve estado por defecto.
export async function getTrialStatus(uid: string): Promise<TrialStatus> {
  const defaultStatus: TrialStatus = {
    hasActiveTrial: false,
    trialExpiresAt: null,
    daysRemaining: 0,
    hasUsedTrial: false
  };

  try {
    const trialRef = doc(collection(db, 'proTrials'), uid);
    const trialSnap = await getDoc(trialRef);

    if (!trialSnap.exists()) {
      return defaultStatus;
    }

    const data = trialSnap.data() as TrialData;
    const expiresAt = data.expiresAt?.toDate?.() ?? null;

    if (!expiresAt) {
      return { ...defaultStatus, hasUsedTrial: data.usedFirstTrial ?? false };
    }

    const now = new Date();
    const isExpired = expiresAt <= now;
    const msRemaining = Math.max(0, expiresAt.getTime() - now.getTime());
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

    // Si el trial está marcado activo pero ya expiró → actualizarlo
    if (data.active && isExpired) {
      try {
        await updateDoc(trialRef, { active: false });
        console.log('[TRIAL] Trial expired — marked inactive for:', uid);
      } catch (updateErr) {
        console.warn('[TRIAL] Could not update expired trial status:', updateErr);
      }
    }

    return {
      hasActiveTrial: data.active && !isExpired,
      trialExpiresAt: expiresAt,
      daysRemaining: isExpired ? 0 : daysRemaining,
      hasUsedTrial: data.usedFirstTrial ?? false
    };
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      // Parar y reportar — requiere actualizar Firestore rules
      console.error('[TRIAL] ⛔ PERMISSION DENIED al leer proTrials. Revisar Firestore rules antes de continuar:', err.message);
    } else {
      console.warn('[TRIAL] Error reading trial status (non-blocking):', err);
    }
    return defaultStatus;
  }
}

// ── Formatear fecha de expiración en español ──
// Ej: "15 de septiembre de 2025"
export function formatTrialExpiresAt(expiresAt: Date | null): string {
  if (!expiresAt) return '';
  return expiresAt.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}
